import React, { useEffect, useState } from "react";
import { Box, Typography, LinearProgress, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import LoadingButton from "@mui/lab/LoadingButton";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import { useStyledColumns } from "./useStyledColumns";
import { useExportExcel } from "./useExportExcel";
import { createSkeletonColumns, skeletonRows } from "./SkeletonUtils";
import IconResolver from "../IconResolver";

const SimpleTable = ({
  data,
  loading,
  isFetching,
  columns,
  actions = [],
  redirect,
  redirectText,
  tableName,
  mode = "default",
  selectionModel,
  onSelectionChange,
  hideFooter = false,
  hideHeaderActions = false,
  onRefresh,
  variant = "default",
}) => {
  const [rows, setRows] = useState(data);
  const [showAll, setShowAll] = useState(false);
  const [internalSelection, setInternalSelection] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});

  const selectedRows = selectionModel ?? internalSelection;

  const handleSelectionChange = (ids) => {
    onSelectionChange?.(ids);

    if (!selectionModel) {
      setInternalSelection(ids);
    }
  };

  const isHidden = (row) => {
    if (typeof row.visibility === "boolean") {
      return row.visibility === false;
    }

    if (typeof row.Visibility === "string") {
      return row.Visibility === "Oculto";
    }

    return false;
  };

  useEffect(() => {
    setRows(data);
  }, [data]);

  const hasAnyAction =
    actions.length > 0 &&
    rows.some((row) => actions.some((a) => a.isVisible?.(row)));

  const styledColumns = useStyledColumns(columns, actions, hasAnyAction);

  const activeRows = showAll ? rows : rows.filter((r) => !isHidden(r));

  const countOculto = data.filter((r) => isHidden(r)).length;

  const exportExcel = useExportExcel(
    rows,
    activeRows,
    columns,
    columnVisibility,
    tableName,
  );

  const skeletonColumns = createSkeletonColumns(styledColumns);
  const isListingVariant = variant === "listing";

  return (
    <Box
      p={isListingVariant ? 0 : 3}
      display="flex"
      flexDirection="column"
      gap={isListingVariant ? 1.5 : 1}
      sx={
        isListingVariant
          ? {
              px: { xs: 2, sm: 3 },
              pb: { xs: 2, sm: 3 },
            }
          : undefined
      }
    >
      {/* HEADER */}
      {!hideHeaderActions && (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            mb: 0.5,
          }}
        >
          {onRefresh && (
            <IconButton
              onClick={onRefresh}
              disabled={isFetching}
              sx={{
                color: isListingVariant ? "#5f48f6" : "inherit",
                height: 40,
                minWidth: isListingVariant ? 72 : 100,
                borderRadius: 999,
                border: "1px solid #d6cfff",
                backgroundColor: "#fff",
                boxShadow: isListingVariant
                  ? "0 8px 18px rgba(15, 23, 42, 0.035)"
                  : "none",

                "&:hover": {
                  backgroundColor: isListingVariant ? "#f7f4ff" : "grey.200",
                  borderColor: isListingVariant ? "#bcaeff" : "grey.400",
                },

                "&:active": {
                  backgroundColor: isListingVariant ? "#f1edff" : "grey.300",
                },
              }}
            >
              {isListingVariant ? (
                <RefreshRoundedIcon
                  sx={{
                    fontSize: 20,
                    transition: "transform 180ms ease",
                    transform: isFetching ? "rotate(120deg)" : "none",
                  }}
                />
              ) : (
                <IconResolver name="refresh" />
              )}
            </IconButton>
          )}
          {/* Botão padrão */}
          <LoadingButton
            variant="contained"
            startIcon={<IconResolver name="plus" color={"#fff"} />}
            onClick={redirect}
            sx={
              isListingVariant
                ? {
                    backgroundColor: "#5f48f6",
                    borderRadius: "999px",
                    boxShadow: "0 10px 22px rgba(95, 72, 246, 0.18)",
                    fontWeight: 700,
                    minHeight: 40,
                    px: 2.5,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#4f3bdc",
                      boxShadow: "0 12px 26px rgba(95, 72, 246, 0.22)",
                    },
                  }
                : undefined
            }
          >
            {redirectText}
          </LoadingButton>

          {/* Botão de refresh (à direita) */}
        </Box>
      )}

      {isFetching && (
        <LinearProgress
          sx={{
            height: 2,
            borderRadius: 12,
          }}
        />
      )}

      <DataGrid
        rows={loading ? skeletonRows : activeRows}
        columns={loading ? skeletonColumns : styledColumns}
        checkboxSelection
        disableMultipleRowSelection={mode === "selection"}
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={handleSelectionChange}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={setColumnVisibility}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        density="comfortable"
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        getRowClassName={(r) => (isHidden(r.row) ? "row-oculto" : "")}
        sx={{
          backgroundColor: "#FFF",
          ...(isListingVariant && {
            border: "1px solid #dce4ee",
            borderRadius: "14px",
            boxShadow: "0 18px 42px rgba(15, 23, 42, 0.055)",
            color: "#172033",
            overflow: "hidden",
          }),

          ...(isListingVariant && {
            "& .MuiDataGrid-main": {
              backgroundColor: "#fff",
            },

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#fbfcfe",
              borderBottom: "1px solid #e2e8f0",
              minHeight: "56px !important",
            },

            "& .MuiDataGrid-columnHeader": {
              outline: "none",
            },

            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#172033",
              fontSize: 13,
              fontWeight: 700,
            },

            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #edf1f6",
              color: "#334155",
              fontSize: 13,
              outline: "none",
            },

            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f8fafc",
            },

            "& .MuiCheckbox-root": {
              color: "#9fb0c6",
            },

            "& .MuiCheckbox-root.Mui-checked": {
              color: "#5f48f6",
            },

            "& .MuiDataGrid-footerContainer": {
              backgroundColor: "#fff",
              borderTop: "1px solid #e2e8f0",
              minHeight: 54,
            },
          }),

          "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
            visibility: "hidden",
            width: 0,
          },

          "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
            visibility: "hidden",
            width: 0,
          },

          "@media (hover: hover) and (pointer: fine)": {
            "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-iconButtonContainer":
              {
                visibility: "visible",
                width: "auto",
              },
            "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon": {
              visibility: "visible",
              width: "auto",
            },
          },

          "& .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-iconButtonContainer":
            {
              visibility: "visible",
              width: "auto",
            },
          "& .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-menuIcon": {
            visibility: "visible",
            width: "auto",
          },
        }}
      />

      {/* FOOTER */}
      {!hideFooter && (
        <Box
          sx={{
            alignItems: { xs: "flex-start", sm: "center" },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            justifyContent: "space-between",
            px: isListingVariant ? 0.5 : 0,
          }}
        >
          <LoadingButton
            variant="outlined"
            size="small"
            onClick={() => exportExcel(selectedRows)}
            sx={
              isListingVariant
                ? {
                    borderColor: "#cbd6e2",
                    borderRadius: "999px",
                    color: "#334155",
                    fontWeight: 600,
                    px: 1.75,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                      borderColor: "#94a3b8",
                    },
                  }
                : undefined
            }
          >
            Baixar tabela
          </LoadingButton>

          <Typography
            sx={{
              color: "#64748b",
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {countOculto > 0 ? (
              <>
                {countOculto} oculto(s) (
                <span
                  onClick={() => setShowAll(!showAll)}
                  style={{ color: "#8142FC", cursor: "pointer" }}
                >
                  {showAll ? "Ocultar" : "Exibir"}
                </span>
                )
              </>
            ) : (
              <>Total de {activeRows.length} itens</>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SimpleTable;
